import { ComboField } from '@components/Fields/ComboField';
import { DocumentoField, DocumentoMaskType } from '@components/Fields/DocumentoField';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { SearchInputAdornment } from '@components/SearchInputAdornment';
import { IForm } from '@hooks/useForm';
import { ILoading } from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useModalWindow } from '@hooks/useModalWindow';
import { IValueState, useValueState } from '@hooks/useValueState';
import { makeStyles } from '@material-ui/core';
import { ICliente } from '@model/Cliente';
import { EnumClienteTipo, IComboClienteTipo } from '@model/ClienteTipo';
import { IValidarDocumentoResponse, useContratoClienteService } from '@services/ContratoClienteService';
import { useThemeColor } from '@themes/Colors';
import { validCNPJ, validCPF } from '@validations/Yup';
import clsx from 'clsx';
import React from 'react';
import { SuccessMessage } from '../SuccessMessage';
import { WarningMessage } from '../WarningMessage';
import { ClienteDebitoModal } from './ClienteDebitoModal';

export type ClienteSearchProps = {
    clienteValueState: IValueState<IValidarDocumentoResponse>
    clienteTipoCombo: IComboClienteTipo
    clienteTipoFieldRef: React.MutableRefObject<HTMLInputElement | undefined>    
    form: IForm<Partial<ICliente>>
    loading: ILoading
}

export function ClienteSearch ({ loading, clienteValueState, clienteTipoCombo, clienteTipoFieldRef, form }: ClienteSearchProps) {
    const { fields } = form;

    const isPessoaJuridica = React.useMemo(() => fields.clienteTipoId.value === EnumClienteTipo.PessoaJuridica, [fields.clienteTipoId.value]);

    const [maskType, setMaskType] = React.useState<DocumentoMaskType>(isPessoaJuridica ? "cnpj" : "cpf");
    const [documentoLabel, setDocumentoLabel] = React.useState<string>(isPessoaJuridica ? "CNPJ" : "CPF");


    const themeColor = useThemeColor();
    const cls = useStyles();
    const contratoClienteService = useContratoClienteService();
    const message = useMessage();
    const debitoModal = useModalWindow();

    const searched = clienteValueState.value !== undefined && clienteValueState.value?.cliente !== null;
    const clienteFound = !!clienteValueState.value?.cliente;

    const localState = useValueState<IValidarDocumentoResponse>();

    const handleSearch = React.useCallback((numero?: string) => {
        if (!loading.isLoading) {
            const value = fields.cpf.getInputValue();
            const isDocumentoValido = !!value && (isPessoaJuridica ? validCNPJ(value) : validCPF(value));

            if (isDocumentoValido) {
                loading.start();
                contratoClienteService.validarDocumento(value)
                    .then(response => {
                        if (response.data.possuiDebitos > 0) {
                            localState.set(response.data);
                            debitoModal.open();
                        }
                        else {
                            clienteValueState.set(response.data);
                        
                            if (response.data.cliente !== undefined) {
                                form.setValues(response.data.cliente!);
                            }
                        }
                    })
                    .catch(message.showException)
                    .finally(loading.stop);
            }
        }
    }, [loading, fields.cpf, isPessoaJuridica, contratoClienteService, message.showException, localState, debitoModal, clienteValueState, form]);

    const handleDebitoContinue = React.useCallback(() => {
        clienteValueState.set(localState.value);
        form.setValues(localState.value!.cliente!);
        debitoModal.close();
    }, [clienteValueState, debitoModal, form, localState.value]);

    React.useEffect(() => {
        const mask = isPessoaJuridica ? "cnpj" : "cpf";
        const documentoLabel = isPessoaJuridica ? "CNPJ" : "CPF";

        setMaskType(mask);
        setDocumentoLabel(documentoLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPessoaJuridica]);

    return (
        <HorizontalGrid className={clsx({
            [cls.stepContainer]: true,
            [themeColor.component.successPanel]: searched && clienteFound,
            [themeColor.component.warningPanel]: searched && !clienteFound,
        })}>
            <GridItem xs={12}>
                <HorizontalGrid spacing={2} verticalAlign="center">
                    <GridItem xs={3}>
                        <ComboField 
                            field={fields.clienteTipoId}
                            inputRef={clienteTipoFieldRef}
                            label="Tipo de Pessoa"
                            required
                            disabled={searched}
                            updateFieldStateOnChange
                        >
                            {clienteTipoCombo.createOptions()}
                        </ComboField>
                    </GridItem>

                    <GridItem xs={3}>
                        <DocumentoField 
                            field={fields.cpf}
                            label={documentoLabel}
                            maskType={maskType}
                            required
                            disabled={searched}
                            updateFieldStateOnChange
                            InputProps={{
                                endAdornment: <SearchInputAdornment onButtonClick={handleSearch} disabled={searched || !(isPessoaJuridica ? validCNPJ(fields.cpf.value) : validCPF(fields.cpf.value))} />
                            }}
                        />
                    </GridItem>

                    {
                        searched && (
                            <GridItem xs={6}>
                                {
                                    clienteFound
                                    ? (
                                        <SuccessMessage
                                            message="Cliente já cadastrado" 
                                        />
                                    )
                                    : (
                                        <WarningMessage
                                            message="Cliente não encontrado, é necessário cadastrá-lo" 
                                        />
                                    )
                                }
                            </GridItem>
                        )
                    }
                </HorizontalGrid>
            </GridItem>

            <ClienteDebitoModal 
                modal={debitoModal}
                onContinue={handleDebitoContinue}
            />
        </HorizontalGrid>        
    )
}

const useStyles = makeStyles(theme => ({
    stepContainer: {
        padding: `${theme.spacing(1)}px ${theme.spacing(4)}px ${theme.spacing(1)}px ${theme.spacing(4)}px`
    }
}));